import React from "react";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { keepPreviousData } from "@tanstack/react-query";
import { trpc } from "~/lib/trpc";
import { ListView } from "~/components/scan/ListView";
import { ScatterView } from "~/components/scan/ScatterView";
import { MarketChart } from "~/components/scan/MarketChart";
import { CompareProvider, useCompare } from "~/components/scan/compare";
import { RangeFiltersBar } from "~/components/scan/RangeFiltersBar";
import {
  applyDealRangeFilters,
  computeDealRangeBounds,
  EMPTY_RANGE_FILTERS,
  hasActiveRangeFilters,
  type DealRangeFilters,
} from "~/lib/deal-filters";
import { cn, formatNumber, type ReportingCurrency } from "~/lib/utils";
import { useT } from "~/lib/i18n";

type View = "list" | "scatter";
type SortMode = "score" | "price_asc" | "price_desc" | "year_desc" | "km_asc" | "recent";

const SORT_OPTIONS: { value: SortMode; label: string; icon: string }[] = [
  { value: "score", label: "Affaires", icon: "★" },
  { value: "price_asc", label: "Prix ↑", icon: "€" },
  { value: "price_desc", label: "Prix ↓", icon: "€" },
  { value: "year_desc", label: "Récent", icon: "📅" },
  { value: "km_asc", label: "Km ↑", icon: "⊙" },
  { value: "recent", label: "Nouveau", icon: "✦" },
];

const RATING_FILTERS = [
  { value: null, label: "Tous", color: "" },
  { value: "super", label: "Super", color: "var(--deal-super)" },
  { value: "good", label: "Bon", color: "var(--deal-good)" },
  { value: "fair", label: "Correct", color: "var(--deal-fair)" },
  { value: "high", label: "Cher", color: "var(--deal-high)" },
  { value: "over", label: "Trop cher", color: "var(--deal-over)" },
] as const;

function isLegacySlug(id: string): boolean {
  return /^[a-z]+-[a-z0-9-]+$/.test(id) && id.length > 12;
}

// --- URL-synced filter state ---
function parseIntParam(v: string | null): number | null {
  if (!v) return null;
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
}

function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const view: View = (searchParams.get("v") as View) || "list";
  const sort: SortMode = (searchParams.get("s") as SortMode) || "score";
  const currency: ReportingCurrency = (searchParams.get("c") as ReportingCurrency) || "EUR";
  const ratingFilter: string | null = searchParams.get("r") || null;
  const versionFilter: string | null = searchParams.get("ver") || null;
  const rangeFilters: DealRangeFilters = {
    priceMin: parseIntParam(searchParams.get("pmin")),
    priceMax: parseIntParam(searchParams.get("pmax")),
    kmMin: parseIntParam(searchParams.get("kmin")),
    kmMax: parseIntParam(searchParams.get("kmax")),
  };

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v == null || v === "") next.delete(k);
          else next.set(k, v);
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const setView = useCallback((v: View) => update({ v: v === "list" ? null : v }), [update]);
  const setSort = useCallback((s: SortMode) => update({ s: s === "score" ? null : s }), [update]);
  const setCurrency = useCallback((c: ReportingCurrency) => update({ c: c === "EUR" ? null : c }), [update]);
  const setRatingFilter = useCallback((r: string | null) => update({ r }), [update]);
  const setVersionFilter = useCallback((ver: string | null) => update({ ver }), [update]);
  const setRangeFilters = useCallback(
    (f: DealRangeFilters) =>
      update({
        pmin: f.priceMin != null ? String(f.priceMin) : null,
        pmax: f.priceMax != null ? String(f.priceMax) : null,
        kmin: f.kmMin != null ? String(f.kmMin) : null,
        kmax: f.kmMax != null ? String(f.kmMax) : null,
      }),
    [update],
  );

  return {
    view, setView,
    sort, setSort,
    currency, setCurrency,
    ratingFilter, setRatingFilter,
    versionFilter, setVersionFilter,
    rangeFilters, setRangeFilters,
  };
}

export default function ScanDashboard(): React.JSX.Element {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useT();
  const utils = trpc.useUtils();

  const {
    view, setView,
    sort, setSort,
    currency, setCurrency,
    ratingFilter, setRatingFilter,
    versionFilter, setVersionFilter,
    rangeFilters, setRangeFilters,
  } = useUrlFilters();

  const createFromSlug = trpc.scan.createFromSlug.useMutation();
  useEffect(() => {
    if (isLegacySlug(id)) {
      const parts = id.split("-");
      const brandSlug = parts[0]!;
      const modelSlug = parts.slice(1).join("-");
      createFromSlug.mutateAsync({ brandSlug, modelSlug }).then((res) => {
        navigate(`/scan/${res.id}`, { replace: true });
      });
    }
  }, [id]);

  const scanQ = trpc.scan.get.useQuery(
    { id },
    {
      enabled: !isLegacySlug(id) && id.length > 0,
      refetchInterval: (q) => {
        const status = (q.state.data as { status?: string } | null | undefined)?.status;
        if (status === "pending" || status === "scraping") return 2_000;
        return 60_000;
      },
    },
  );

  const refreshMutation = trpc.scan.refresh.useMutation({
    onMutate: async () => {
      await utils.scan.get.cancel({ id });
      const previous = utils.scan.get.getData({ id });
      if (previous) {
        utils.scan.get.setData({ id }, {
          ...previous,
          status: "pending",
          scrapeSourcesTotal: 0,
          scrapeSourcesDone: 0,
          scrapeListingsFound: 0,
          scrapeStartedAt: null,
          scrapeFinishedAt: null,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) utils.scan.get.setData({ id }, ctx.previous);
    },
    onSuccess: (data) => {
      if (data && "error" in data) {
        void utils.scan.get.invalidate({ id });
        return;
      }
      void utils.scan.get.invalidate({ id });
      // Refetch without invalidating — avoids blanking the list between polls.
      void utils.scan.results.refetch({ id, limit: 200, sort });
    },
  });

  const scan = scanQ.data;
  const scanStatus = scan?.status ?? "ready";
  const isRunning =
    scanStatus === "pending" ||
    scanStatus === "scraping" ||
    refreshMutation.isPending;

  useEffect(() => {
    if (scan?.name) document.title = `${scan.name} — MotorScan`;
    return () => { document.title = "MotorScan — The EU car scanner"; };
  }, [scan?.name]);

  const resultsQ = trpc.scan.results.useQuery(
    { id, limit: 200, sort },
    {
      enabled: !isLegacySlug(id) && id.length > 0,
      refetchInterval: isRunning ? 3_000 : 60_000,
      placeholderData: keepPreviousData,
    },
  );

  const excludedQ = trpc.scan.excludedCount.useQuery(
    { scanId: id },
    { enabled: !isLegacySlug(id) && id.length > 0 },
  );

  const excludeMutation = trpc.scan.excludeListing.useMutation({
    onSuccess: () => {
      void utils.scan.results.refetch({ id, limit: 200, sort });
      void utils.scan.excludedCount.invalidate({ scanId: id });
    },
  });

  // Keep the last non-empty result set visible while scraping so refetch
  // cycles (or taxonomy lag on fresh listings) don't flash an empty list.
  const cachedDealsRef = useRef<NonNullable<typeof resultsQ.data>["items"]>([]);
  const freshDeals = resultsQ.data?.items ?? [];
  useEffect(() => {
    if (freshDeals.length > 0) cachedDealsRef.current = freshDeals;
    if (!isRunning) cachedDealsRef.current = freshDeals;
  }, [freshDeals, isRunning]);

  const allDeals = useMemo(() => {
    if (isRunning && freshDeals.length === 0 && cachedDealsRef.current.length > 0) {
      return cachedDealsRef.current;
    }
    return freshDeals;
  }, [freshDeals, isRunning]);

  const rangeBounds = useMemo(() => computeDealRangeBounds(allDeals), [allDeals]);

  // Compute unique versions present in the result set
  const versionOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of allDeals) {
      const vn = (d as { versionName?: string | null }).versionName;
      if (vn) map.set(vn, vn);
    }
    return Array.from(map.values()).sort();
  }, [allDeals]);

  const deals = useMemo(() => {
    let rows = allDeals;
    if (versionFilter) {
      rows = rows.filter((d) => (d as { versionName?: string | null }).versionName === versionFilter);
    }
    rows = applyDealRangeFilters(rows, rangeFilters);
    if (ratingFilter) rows = rows.filter((d) => d.dealRating === ratingFilter);
    return rows;
  }, [allDeals, versionFilter, rangeFilters, ratingFilter]);

  const hasRangeFilter = hasActiveRangeFilters(rangeFilters);

  const showResults = isRunning || allDeals.length > 0 || (resultsQ.isPending && !resultsQ.data);
  const listLoading = resultsQ.isPending && allDeals.length === 0;
  const deleteMutation = trpc.scan.delete.useMutation({
    onSuccess: () => navigate("/account"),
  });

  const handleExclude = (listingId: string) => {
    excludeMutation.mutate({ scanId: id, listingId });
  };

  if (isLegacySlug(id)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative h-6 w-6">
          <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <CompareProvider currency={currency}>
      <ScanDashboardBody
        scan={scan ?? undefined}
        currency={currency}
        setCurrency={setCurrency}
        onRefresh={() => refreshMutation.mutate({ id })}
        refreshing={refreshMutation.isPending}
        onDelete={() => {
          if (confirm("Supprimer ce scan définitivement ?")) deleteMutation.mutate({ id });
        }}
        isRunning={isRunning}
        scanStatus={scanStatus}
        showResults={showResults}
        listLoading={listLoading}
        deals={deals}
        allDeals={allDeals}
        view={view}
        setView={setView}
        sort={sort}
        setSort={setSort}
        ratingFilter={ratingFilter}
        setRatingFilter={setRatingFilter}
        versionFilter={versionFilter}
        setVersionFilter={setVersionFilter}
        versionOptions={versionOptions}
        rangeFilters={rangeFilters}
        setRangeFilters={setRangeFilters}
        rangeBounds={rangeBounds}
        hasRangeFilter={hasRangeFilter}
        excludedCount={excludedQ.data?.count ?? 0}
        onExclude={handleExclude}
        excludingId={excludeMutation.isPending ? excludeMutation.variables?.listingId : undefined}
        resultsPending={resultsQ.isPending}
        sourcesTotal={scan?.scrapeSourcesTotal ?? 0}
        onRetry={() => refreshMutation.mutate({ id })}
        resolvedBrandId={(scan as { resolvedBrandId?: string | null })?.resolvedBrandId ?? null}
        resolvedModelId={(scan as { resolvedModelIds?: string[] })?.resolvedModelIds?.[0] ?? null}
        country={scan?.filters?.countries?.[0] ?? null}
      />
    </CompareProvider>
  );
}

function ScanDashboardBody({
  scan,
  currency,
  setCurrency,
  onRefresh,
  refreshing,
  onDelete,
  isRunning,
  scanStatus,
  showResults,
  listLoading,
  deals,
  allDeals,
  view,
  setView,
  sort,
  setSort,
  ratingFilter,
  setRatingFilter,
  versionFilter,
  setVersionFilter,
  versionOptions,
  rangeFilters,
  setRangeFilters,
  rangeBounds,
  hasRangeFilter,
  excludedCount,
  onExclude,
  excludingId,
  resultsPending,
  sourcesTotal,
  onRetry,
  resolvedBrandId,
  resolvedModelId,
  country,
}: {
  scan: { name?: string | null; scrapeSourcesTotal?: number } | undefined;
  currency: ReportingCurrency;
  setCurrency: (c: ReportingCurrency) => void;
  onRefresh: () => void;
  refreshing: boolean;
  onDelete: () => void;
  isRunning: boolean;
  scanStatus: string;
  showResults: boolean;
  listLoading: boolean;
  deals: Parameters<typeof ListView>[0]["deals"];
  allDeals: Parameters<typeof ListView>[0]["deals"];
  view: View;
  setView: (v: View) => void;
  sort: SortMode;
  setSort: (s: SortMode) => void;
  ratingFilter: string | null;
  setRatingFilter: (r: string | null) => void;
  versionFilter: string | null;
  setVersionFilter: (v: string | null) => void;
  versionOptions: string[];
  rangeFilters: DealRangeFilters;
  setRangeFilters: (f: DealRangeFilters) => void;
  rangeBounds: ReturnType<typeof computeDealRangeBounds>;
  hasRangeFilter: boolean;
  excludedCount: number;
  onExclude: (listingId: string) => void;
  excludingId?: string;
  resultsPending: boolean;
  sourcesTotal: number;
  onRetry: () => void;
  resolvedBrandId: string | null;
  resolvedModelId: string | null;
  country: string | null | undefined;
}): React.JSX.Element {
  const { panelOpen, panelWidth, items: compareItems } = useCompare();
  const comparePad = panelOpen && compareItems.length > 0;

  return (
    <div
      className={cn(
        "flex flex-col min-h-[calc(100dvh-3rem)] transition-[padding] duration-200",
        compareItems.length > 0 ? "pb-[220px] sm:pb-[200px]" : "pb-[180px] sm:pb-[160px]",
      )}
      style={comparePad ? { paddingRight: panelWidth } : undefined}
    >
      <DashboardHeader
        scanName={scan?.name}
        scan={scan}
        currency={currency}
        setCurrency={setCurrency}
        onRefresh={onRefresh}
        refreshing={refreshing}
        onDelete={onDelete}
      />

      {isRunning && <ScanProgressBanner scan={scan} />}
      {scanStatus === "error" && <ScanErrorBanner onRetry={onRetry} />}
      {scanStatus === "ready" && !resultsPending && deals.length === 0 && allDeals.length === 0 && (
        <ScanEmptyState sourcesTotal={sourcesTotal} />
      )}

      {showResults && (
        <>
          <Toolbar
            view={view}
            setView={setView}
            totalCount={deals.length}
            allCount={allDeals.length}
            excludedCount={excludedCount}
            sort={sort}
            setSort={setSort}
            ratingFilter={ratingFilter}
            setRatingFilter={setRatingFilter}
            versionFilter={versionFilter}
            setVersionFilter={setVersionFilter}
            versionOptions={versionOptions}
            hasRangeFilter={hasRangeFilter}
            rangeFilters={rangeFilters}
            rangeBounds={rangeBounds}
            setRangeFilters={setRangeFilters}
          />
          {allDeals.length > 0 && (
            <CohortStrip
              allDeals={deals}
              allCount={allDeals.length}
              ratingFilter={ratingFilter}
              setRatingFilter={setRatingFilter}
            />
          )}
          <div className="flex-1 min-h-0">
            {view === "list" && (
              <ListView
                deals={deals}
                loading={listLoading}
                currency={currency}
                onExclude={onExclude}
                excludingId={excludingId}
              />
            )}
            {view === "scatter" && <ScatterView deals={deals} />}
          </div>
          <MarketChart
            brandId={resolvedBrandId}
            modelId={resolvedModelId}
            country={country ?? null}
          />
        </>
      )}
    </div>
  );
}

/**
 * Cohort context strip — answers "what does this market look like?" in one
 * line. Shows the breakdown (super / good / fair / high / over), the median
 * price, and acts as a clickable filter (clicking "super" applies the rating
 * filter — same as the toolbar pills, but with the count visible).
 */
function CohortStrip({
  allDeals,
  allCount,
  ratingFilter,
  setRatingFilter,
}: {
  allDeals: Parameters<typeof ListView>[0]["deals"];
  allCount: number;
  ratingFilter: string | null;
  setRatingFilter: (r: string | null) => void;
}): React.JSX.Element {
  const { numberLocale } = useT();
  const stats = useMemo(() => {
    const counts: Record<string, number> = { super: 0, good: 0, fair: 0, high: 0, over: 0, unknown: 0 };
    const prices: number[] = [];
    let suspect = 0;
    for (const d of allDeals) {
      const r = (d.dealRating ?? "unknown") as keyof typeof counts;
      counts[r] = (counts[r] ?? 0) + 1;
      if (d.currentPriceEur != null) prices.push(d.currentPriceEur);
      if ((d as { savingsSuspect?: boolean }).savingsSuspect) suspect++;
    }
    prices.sort((a, b) => a - b);
    const median = prices.length > 0 ? prices[Math.floor(prices.length / 2)] : null;
    return { counts, median, total: allDeals.length, suspect };
  }, [allDeals]);

  const formatEurCompact = (v: number): string =>
    new Intl.NumberFormat(numberLocale, {
      style: "currency",
      currency: "EUR",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(v);

  const items: Array<{ rating: string; label: string; color: string; count: number }> = [
    { rating: "super", label: "Super", color: "var(--deal-super)", count: stats.counts.super! },
    { rating: "good", label: "Bon", color: "var(--deal-good)", count: stats.counts.good! },
    { rating: "fair", label: "Correct", color: "var(--deal-fair)", count: stats.counts.fair! },
    { rating: "high", label: "Cher", color: "var(--deal-high)", count: stats.counts.high! },
    { rating: "over", label: "Trop cher", color: "var(--deal-over)", count: stats.counts.over! },
  ];

  return (
    <div className="border-b border-[var(--divider-weak)] bg-[var(--surface-card)]/60">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 flex-wrap text-[12px]">
        <span className="text-ink-tertiary mono">
          {formatNumber(stats.total, numberLocale)}
          {stats.total < allCount ? ` / ${formatNumber(allCount, numberLocale)}` : ""} sur le marché
        </span>
        <span className="opacity-30 hidden sm:inline">·</span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {items
            .filter((it) => it.count > 0)
            .map((it) => {
              const active = ratingFilter === it.rating;
              return (
                <button
                  key={it.rating}
                  type="button"
                  onClick={() => setRatingFilter(active ? null : it.rating)}
                  className={cn(
                    "inline-flex items-center gap-1.5 px-2 py-1 rounded-md transition-colors",
                    active
                      ? "bg-[var(--surface-hover)] ring-1 ring-[var(--divider-strong)]"
                      : "hover:bg-[var(--surface-hover)]/50",
                  )}
                  aria-pressed={active}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ background: it.color }}
                  />
                  <span className="mono font-medium text-ink-primary">{it.count}</span>
                  <span className="text-ink-tertiary">{it.label}</span>
                </button>
              );
            })}
        </div>
        {stats.median != null && (
          <>
            <span className="opacity-30 hidden sm:inline">·</span>
            <span className="text-ink-tertiary">
              Médiane <span className="mono font-medium text-ink-primary">{formatEurCompact(stats.median)}</span>
            </span>
          </>
        )}
        {stats.suspect > 0 && (
          <>
            <span className="opacity-30 hidden sm:inline">·</span>
            <span
              className="text-amber-700 dark:text-amber-400"
              title="Annonces dont l'écart au marché est suspect (cohorte trop large ou peu fiable)"
            >
              {stats.suspect} à vérifier
            </span>
          </>
        )}
      </div>
    </div>
  );
}

function ScanProgressBanner({ scan }: { scan: any }): React.JSX.Element {
  const { t } = useT();
  const done = scan?.scrapeSourcesDone ?? 0;
  const total = scan?.scrapeSourcesTotal ?? 0;
  const found = scan?.scrapeListingsFound ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="border-b border-[var(--divider-weak)] bg-[var(--surface-card)]">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="relative h-5 w-5 shrink-0">
            <div className="absolute inset-0 rounded-full border-2 border-accent border-t-transparent animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-ink-primary font-medium">
              {scan?.status === "pending"
                ? t("web.scan.status.pending")
                : t("web.scan.status.scraping", { done: String(done), total: String(total), found: String(found) })}
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-[var(--surface-sunken)] overflow-hidden">
              {total > 0 ? (
                <div
                  className="h-full rounded-full bg-accent transition-[width] duration-700 ease-out"
                  style={{ width: `${pct}%` }}
                />
              ) : (
                <div className="h-full w-2/5 rounded-full bg-accent animate-pulse" />
              )}
            </div>
          </div>
          <span className="text-xs mono text-ink-tertiary shrink-0">{pct}%</span>
        </div>
      </div>
    </div>
  );
}

function ScanErrorBanner({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useT();
  return (
    <div className="border-b border-red-200 bg-red-50 dark:bg-red-950/20">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <span className="shrink-0 text-red-500">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
            </svg>
          </span>
          <p className="text-sm text-red-700 dark:text-red-300 truncate">{t("web.scan.status.error")}</p>
        </div>
        <button
          onClick={onRetry}
          className="shrink-0 h-10 px-5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 active:scale-[0.97] transition-all"
        >
          {t("web.scan.status.retry")}
        </button>
      </div>
    </div>
  );
}

function ScanEmptyState({ sourcesTotal }: { sourcesTotal: number }): React.JSX.Element {
  const { t } = useT();
  return (
    <div className="flex-1 flex items-center justify-center min-h-[40vh] px-4">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-[var(--surface-sunken)] flex items-center justify-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--ink-tertiary)" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
          </svg>
        </div>
        <h2 className="text-lg font-medium text-ink-primary">{t("web.scan.done.zero.title")}</h2>
        <p className="mt-2 text-sm text-ink-secondary leading-relaxed">{t("web.scan.done.zero.body", { total: String(sourcesTotal) })}</p>
      </div>
    </div>
  );
}

function DashboardHeader({
  scanName, scan, currency, setCurrency, onRefresh, refreshing, onDelete,
}: {
  scanName: string | null | undefined;
  scan: any;
  currency: ReportingCurrency;
  setCurrency: (c: ReportingCurrency) => void;
  onRefresh: () => void;
  refreshing: boolean;
  onDelete: () => void;
}): React.JSX.Element {
  const { t } = useT();
  const lastRefresh = scan?.lastRefreshAt ? new Date(scan.lastRefreshAt) : null;

  return (
    <header className="border-b border-[var(--divider-weak)] bg-[var(--surface-card)]">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-4 sm:py-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="font-display text-xl sm:text-2xl leading-tight text-ink-primary truncate">
              {scanName ?? t("web.scan.loading")}
            </h1>
            {lastRefresh && (
              <p className="mt-1 text-[11px] text-ink-tertiary mono">
                MAJ {lastRefresh.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })} à {lastRefresh.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onRefresh}
              disabled={refreshing}
              className={cn(
                "h-10 px-4 text-sm font-medium rounded-lg transition-all active:scale-[0.97]",
                "bg-accent text-[var(--ink-on-accent)] hover:bg-[var(--accent-hover)]",
                "disabled:opacity-50 disabled:pointer-events-none",
              )}
            >
              {refreshing ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Scan…
                </span>
              ) : (
                "Actualiser"
              )}
            </button>
            <CurrencyToggle value={currency} onChange={setCurrency} />
            <button
              onClick={onDelete}
              className="h-10 w-10 flex items-center justify-center rounded-lg border border-[var(--divider-strong)] text-ink-tertiary hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors"
              title="Supprimer le scan"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function CurrencyToggle({ value, onChange }: { value: ReportingCurrency; onChange: (c: ReportingCurrency) => void }): React.JSX.Element {
  const currencies: ReportingCurrency[] = ["EUR", "USD", "CHF"];
  return (
    <div className="inline-flex h-10 rounded-lg border border-[var(--divider-strong)] overflow-hidden bg-[var(--surface-page)]">
      {currencies.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          className={cn(
            "min-w-[40px] px-2.5 text-xs mono font-medium transition-colors",
            value === c
              ? "bg-[var(--surface-hover)] text-ink-primary"
              : "text-ink-tertiary hover:text-ink-secondary hover:bg-[var(--surface-hover)]",
          )}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function Toolbar({
  view,
  setView,
  totalCount,
  allCount,
  excludedCount,
  sort,
  setSort,
  ratingFilter,
  setRatingFilter,
  versionFilter,
  setVersionFilter,
  versionOptions,
  hasRangeFilter,
  rangeFilters,
  rangeBounds,
  setRangeFilters,
}: {
  view: View;
  setView: (v: View) => void;
  totalCount: number;
  allCount: number;
  excludedCount: number;
  sort: SortMode;
  setSort: (s: SortMode) => void;
  ratingFilter: string | null;
  setRatingFilter: (r: string | null) => void;
  versionFilter: string | null;
  setVersionFilter: (v: string | null) => void;
  versionOptions: string[];
  hasRangeFilter: boolean;
  rangeFilters: DealRangeFilters;
  rangeBounds: ReturnType<typeof computeDealRangeBounds>;
  setRangeFilters: (f: DealRangeFilters) => void;
}): React.JSX.Element {
  const { numberLocale } = useT();
  return (
    <div className="sticky top-0 z-20 border-b border-[var(--divider-weak)] bg-[color-mix(in_oklab,var(--surface-page)_92%,transparent)] backdrop-blur-md">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 py-3 flex flex-col gap-3">

        {/* Row 1: Counter + View switcher */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-ink-primary tabular">
              {formatNumber(totalCount, numberLocale)}
            </span>
            <span className="text-sm text-ink-secondary">
              {(ratingFilter || hasRangeFilter) && totalCount < allCount
                ? `/ ${formatNumber(allCount, numberLocale)}`
                : "résultats"}
            </span>
            {excludedCount > 0 && (
              <span className="inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[var(--surface-sunken)] text-[11px] text-ink-tertiary mono">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                {excludedCount}
              </span>
            )}
          </div>

          <div className="inline-flex h-10 rounded-lg border border-[var(--divider-strong)] overflow-hidden bg-[var(--surface-page)]">
            {(["list", "scatter"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={cn(
                  "flex items-center justify-center w-11 sm:w-auto sm:px-4 sm:gap-2 text-sm transition-colors",
                  view === v
                    ? "bg-[var(--surface-hover)] text-ink-primary font-medium"
                    : "text-ink-tertiary hover:text-ink-secondary",
                )}
                title={v === "list" ? "Liste" : "Scatter"}
              >
                {v === "list" ? <ListIcon /> : <ScatterIcon />}
                <span className="hidden sm:inline">{v === "list" ? "Liste" : "Scatter"}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Row 2: Version filter (only when multiple versions detected) */}
        {versionOptions.length > 1 && (
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
            <span className="shrink-0 text-[11px] text-ink-tertiary uppercase tracking-wider">Version</span>
            <button
              type="button"
              onClick={() => setVersionFilter(null)}
              className={cn(
                "shrink-0 h-8 px-3 rounded-lg text-[12px] font-medium border transition-all active:scale-[0.96]",
                versionFilter === null
                  ? "border-accent/40 bg-accent text-[var(--ink-on-accent)] shadow-sm"
                  : "border-[var(--divider-strong)] bg-[var(--surface-card)] text-ink-secondary hover:bg-[var(--surface-hover)]",
              )}
            >
              Toutes
            </button>
            {versionOptions.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setVersionFilter(versionFilter === v ? null : v)}
                className={cn(
                  "shrink-0 h-8 px-3 rounded-lg text-[12px] font-medium border transition-all active:scale-[0.96]",
                  versionFilter === v
                    ? "border-accent/40 bg-accent text-[var(--ink-on-accent)] shadow-sm"
                    : "border-[var(--divider-strong)] bg-[var(--surface-card)] text-ink-secondary hover:bg-[var(--surface-hover)]",
                )}
              >
                {v}
              </button>
            ))}
          </div>
        )}

        {/* Row 3: Price & km range filters */}
        <RangeFiltersBar
          filters={rangeFilters}
          onChange={setRangeFilters}
          bounds={rangeBounds}
        />

        {/* Row 4: Sort pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide -mx-1 px-1">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSort(opt.value)}
              className={cn(
                "shrink-0 h-9 sm:h-10 px-3 sm:px-4 rounded-lg text-[13px] sm:text-sm font-medium border transition-all active:scale-[0.96]",
                sort === opt.value
                  ? "border-accent/40 bg-accent text-[var(--ink-on-accent)] shadow-[0_1px_4px_rgba(0,0,0,0.15)]"
                  : "border-[var(--divider-strong)] bg-[var(--surface-card)] text-ink-secondary hover:bg-[var(--surface-hover)] hover:text-ink-primary",
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* Row 4: Deal rating filters */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-hide -mx-1 px-1">
          {RATING_FILTERS.map((f) => (
            <button
              key={f.label}
              onClick={() => setRatingFilter(f.value)}
              className={cn(
                "shrink-0 h-8 sm:h-9 px-3 sm:px-3.5 rounded-full text-[12px] sm:text-[13px] font-medium border transition-all active:scale-[0.96]",
                "inline-flex items-center gap-1.5",
                ratingFilter === f.value
                  ? "border-current/20 bg-current/10 text-ink-primary"
                  : "border-[var(--divider-weak)] text-ink-tertiary hover:text-ink-secondary hover:border-[var(--divider-strong)]",
              )}
              style={ratingFilter === f.value && f.color ? { color: f.color } : undefined}
            >
              {f.color && (
                <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: f.color }} />
              )}
              {f.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ListIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <rect x="1" y="2" width="14" height="2.5" rx="0.75" />
      <rect x="1" y="6.75" width="14" height="2.5" rx="0.75" />
      <rect x="1" y="11.5" width="14" height="2.5" rx="0.75" />
    </svg>
  );
}

function ScatterIcon(): React.JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
      <circle cx="4" cy="10" r="1.8" />
      <circle cx="8" cy="5" r="1.8" />
      <circle cx="12" cy="8" r="1.8" />
      <circle cx="6" cy="12" r="1.3" opacity="0.5" />
      <circle cx="11" cy="3" r="1.3" opacity="0.5" />
    </svg>
  );
}
